import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import * as authService from 'services/cognitoAuthService';
import type { AuthSession } from 'services/cognitoAuthService';
import { IdentityContextValue } from './identity.types';

const IdentityContext = createContext<IdentityContextValue | undefined>(undefined);

export const IdentityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Restore (and refresh if needed) the stored session on page load.
  useEffect(() => {
    let cancelled = false;
    authService.getSession().then((restored) => {
      if (cancelled) return;
      setSession(restored);
      setIsLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  // Silent refresh shortly before the ID token expires, while the tab is open.
  useEffect(() => {
    if (!session) return;
    const delay = Math.max(session.expiresAt - Date.now() - authService.REFRESH_MARGIN_MS, 0);
    const timer = setTimeout(() => {
      authService.refresh(session)
        .then(setSession)
        .catch(() => authService.signOut(session).then(() => setSession(null)));
    }, delay);
    return () => clearTimeout(timer);
  }, [session]);

  const signIn = useCallback(async (email: string, password: string) => {
    setSession(await authService.signIn(email, password));
  }, []);

  const signOut = useCallback(async () => {
    await authService.signOut(session);
    setSession(null);
  }, [session]);

  return (
    <IdentityContext.Provider
      value={{
        isAuth: !!session,
        isLoading,
        email: session?.email ?? '',
        expiresAt: session?.expiresAt ?? null,
        signIn,
        signOut,
      }}
    >
      {children}
    </IdentityContext.Provider>
  );
};

export const useIdentity = (): IdentityContextValue => {
  const context = useContext(IdentityContext);
  if (context === undefined) {
    throw new Error('useIdentity must be used within an IdentityProvider');
  }
  return context;
};

export default IdentityContext;
